/**
 * Website / Email / Phone as labelled links, socials as square icon links
 * with descriptive accessible names (prototype `person-page`).
 */
import type { PublicContact } from "../api";
import { h } from "../dom";

const SVG_NS = "http://www.w3.org/2000/svg";

/** Instagram and Facebook get the prototype's logos; others a text label. */
function logo(kind: "instagram" | "facebook"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", `bsc-contacts__logo bsc-contacts__logo--${kind}`);
  const add = (tag: string, attrs: Record<string, string>) => {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    svg.append(el);
  };
  if (kind === "instagram") {
    add("rect", { x: "3", y: "3", width: "18", height: "18", rx: "5", fill: "none", stroke: "currentColor", "stroke-width": "2" });
    add("circle", { cx: "12", cy: "12", r: "4", fill: "none", stroke: "currentColor", "stroke-width": "2" });
    add("circle", { cx: "17.5", cy: "6.5", r: "1", fill: "currentColor" });
  } else {
    add("path", {
      d: "M14 21v-8h3l.5-3H14V8.5C14 7.5 14.5 7 15.7 7H18V4.2c-.7-.1-1.7-.2-2.8-.2C12.4 4 11 5.7 11 8.3V10H8v3h3v8z",
      fill: "currentColor",
    });
  }
  return svg;
}

/** Only http(s) links leave the page; anything else in the data is dropped. */
function safeUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : null;
  } catch {
    return null;
  }
}

export function contactLinks(contact: PublicContact, owner: string, label: string): HTMLElement | null {
  const links: HTMLElement[] = [];
  const external = (href: string | null, text: string, social?: "instagram" | "facebook") => {
    const safe = safeUrl(href);
    if (!safe) return;
    links.push(
      social
        ? h(
            "a",
            {
              class: "bsc-contacts__link bsc-contacts__link--social",
              href: safe,
              target: "_blank",
              rel: "noopener noreferrer",
              "aria-label": `${owner} on ${text} (opens in a new tab)`,
              title: text,
            },
            logo(social)
          )
        : h(
            "a",
            { class: "bsc-contacts__link", href: safe, target: "_blank", rel: "noopener noreferrer" },
            text,
            h("span", { class: "bsc-sr-only" }, ` for ${owner} (opens in a new tab)`)
          )
    );
  };
  external(contact.websiteUrl, "Website");
  if (contact.email) links.push(h("a", { class: "bsc-contacts__link", href: `mailto:${contact.email}` }, "Email", h("span", { class: "bsc-sr-only" }, ` ${owner}`)));
  if (contact.phone) links.push(h("a", { class: "bsc-contacts__link", href: `tel:${contact.phone.replace(/[^\d+]/g, "")}` }, contact.phone));
  external(contact.instagramUrl, "Instagram", "instagram");
  external(contact.facebookUrl, "Facebook", "facebook");
  external(contact.twitterUrl, "X");
  external(contact.blueskyUrl, "Bluesky");
  external(contact.tiktokUrl, "TikTok");
  external(contact.linkedinUrl, "LinkedIn");
  external(contact.youtubeUrl, "YouTube");
  if (!links.length) return null;
  return h("div", { class: "bsc-contacts", role: "group", "aria-label": label }, links);
}
